import {
  useGetVendors,
  getGetVendorsQueryOptions,
} from "@/requests/gen/react-query/vendor";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ParamDataTableToolbar,
  ParamSearchFilter,
  ParamClearButton,
  ParamDataTable,
  ParamDateFilter,
  dataTableSearchParsers,
} from "@/components/ui/param-data-table";
import { Button } from "@/components/ui/button";
import { ColumnDef } from "@tanstack/react-table";
import {
  createFileRoute,
  Link,
  linkOptions,
  type LinkProps,
} from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { keepPreviousData } from "@tanstack/react-query";
import { Header } from "@/components/universal/header";
import { CountryEnum } from "@/requests/gen/react-query/fastAPI.schemas";
import parsePhoneNumber from "libphonenumber-js";
import {
  createLoader,
  parseAsArrayOf,
  parseAsStringEnum,
  useQueryStates,
  type inferParserType,
} from "nuqs";
import { DropdownFilter } from "@/components/ui/dropdown-filter";
import { countryOptions } from "../-country-options";

const vendorSearchParsers = {
  countries: parseAsArrayOf(parseAsStringEnum(Object.values(CountryEnum))),
};

const vendorSearchAllParsers = {
  ...dataTableSearchParsers,
  ...vendorSearchParsers,
};

const loadVendorSearch = createLoader(vendorSearchAllParsers);

type VendorSearch = inferParserType<typeof vendorSearchAllParsers>;

function vendorQueryParams(
  p: VendorSearch,
): Parameters<typeof getGetVendorsQueryOptions>[0] {
  return {
    page: p.page,
    limit: p.limit,
    search: p.search,
    sort_column: p.sort_column,
    ascending: p.ascending,
    created_at_start: p.created_at_start,
    created_at_end: p.created_at_end,
    country: p.countries,
  };
}

export const Route = createFileRoute("/_private/_warehouse-360/vendor/lookup")({
  loader: ({ context: { queryClient }, location }) =>
    queryClient.prefetchQuery(
      getGetVendorsQueryOptions(
        vendorQueryParams(loadVendorSearch(location.searchStr)),
      ),
    ),
  component: VendorLookup,
});

// Define the columns for the table
interface VendorTableProps {
  link?: LinkProps;
  name: string;
  phone?: string | null;
  email?: string | null;
  country: string;
  entity: string;
}

// Define the columns headers and accessor keys
const columns: ColumnDef<VendorTableProps>[] = [
  { header: "Name", accessorKey: "name" },
  { header: "Phone", accessorKey: "phone" },
  { header: "Email", accessorKey: "email" },
  { header: "Country", accessorKey: "country" },
  { header: "Entity", accessorKey: "entity" },
];

// Main component
function VendorLookup() {
  const [queryParams, setQueryParams] = useQueryStates(vendorSearchAllParsers);

  // Get the vendors
  const vendors = useGetVendors(vendorQueryParams(queryParams), {
    query: {
      placeholderData: keepPreviousData,
    },
  });

  // Map the data to the table format
  const tableData: VendorTableProps[] | undefined = vendors.data?.items.map(
    (vendor) => {
      return {
        link: linkOptions({
          to: "/vendor/$vendorId",
          params: { vendorId: String(vendor.vendor_id) },
        }),
        name: vendor.name,
        phone: parsePhoneNumber(vendor.phone ?? "")?.formatNational(),
        email: vendor.email,
        country: vendor.country,
        entity: vendor.fulfiller.name,
      };
    },
  );

  return (
    <div className="text-left">
      <title>Vendors - Legrande Health Portal</title>
      <Header crumbs={[{ name: "Vendors", link: { to: "/vendor/lookup" } }]} />
      <Card>
        <CardHeader>
          <CardTitle>Vendors</CardTitle>
          <CardDescription>
            View vendors below. You can search, filter, and click to see more
            details.
          </CardDescription>
          <CardAction>
            <Button variant="default" size="sm" asChild>
              <Link to="/vendor/create">
                <Plus />
                Create Vendor
              </Link>
            </Button>
          </CardAction>
        </CardHeader>
        <CardContent>
          <ParamDataTableToolbar>
            <ParamSearchFilter isFetching={vendors.isFetching} />
            <ParamDateFilter title="Created At" name="created_at" />
            <DropdownFilter
              title="Country"
              options={countryOptions}
              values={queryParams.countries || []}
              onChange={(values) => setQueryParams({ countries: values })}
            />
            <ParamClearButton />
          </ParamDataTableToolbar>
          <ParamDataTable
            columns={columns}
            data={tableData ?? []}
            isLoading={vendors.isLoading}
            isError={vendors.isError}
            count={vendors.data?.count}
          />
        </CardContent>
      </Card>
    </div>
  );
}
